// frontend/src/utils/auth.ts
// Authentication utilities and helpers

import { User } from '@/contexts/AuthContext';

export const AUTH_STORAGE_KEYS = {
  TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'auth_user',
  REMEMBER_ME: 'auth_remember_me'
} as const;

export const USER_ROLES = {
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  PROVIDER: 'provider',
  CUSTOMER: 'customer'
} as const;

export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: ['*'], // All permissions
  [USER_ROLES.ADMIN]: [
    'users.view',
    'users.create',
    'users.edit',
    'users.delete',
    'categories.manage',
    'services.manage',
    'providers.manage',
    'bookings.manage',
    'analytics.view',
    'reports.view'
  ],
  [USER_ROLES.PROVIDER]: [
    'profile.edit',
    'services.manage_own',
    'bookings.view_own',
    'bookings.manage_own',
    'areas.manage_own',
    'analytics.view_own'
  ],
  [USER_ROLES.CUSTOMER]: [
    'profile.edit',
    'bookings.create',
    'bookings.view_own',
    'reviews.create',
    'favorites.manage'
  ]
} as const;

/**
 * Check if user has specific permission
 */
export const hasPermission = (user: User | null, permission: string): boolean => {
  if (!user) return false;
  
  const userPermissions = ROLE_PERMISSIONS[user.role as keyof typeof ROLE_PERMISSIONS] || [];
  
  // Super admin has all permissions
  if (userPermissions.includes('*')) return true;
  
  return userPermissions.includes(permission);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user: User | null, roles: string[]): boolean => {
  if (!user) return false;
  return roles.includes(user.role);
};

/**
 * Get user display name
 */
export const getUserDisplayName = (user: User | null): string => {
  if (!user) return 'Guest';
  
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  
  if (user.first_name) return user.first_name;
  if (user.username) return user.username;
  
  return user.email;
};

/**
 * Get user avatar URL or initials
 */
export const getUserAvatar = (user: User | null): { type: 'image' | 'initials'; value: string } => {
  if (!user) return { type: 'initials', value: 'G' };
  
  if (user.profile_image) {
    return { type: 'image', value: user.profile_image };
  }
  
  // Generate initials
  let initials = '';
  if (user.first_name) initials += user.first_name.charAt(0);
  if (user.last_name) initials += user.last_name.charAt(0);
  
  if (!initials && user.username) {
    initials = user.username.charAt(0);
  }
  
  if (!initials && user.email) {
    initials = user.email.charAt(0);
  }
  
  return { type: 'initials', value: initials.toUpperCase() || 'U' };
};

/**
 * Get role display name and color
 */
export const getRoleDisplay = (role: string): { name: string; color: string; bgColor: string } => {
  const roleDisplayMap = {
    [USER_ROLES.SUPER_ADMIN]: { 
      name: 'Super Admin', 
      color: 'text-purple-700', 
      bgColor: 'bg-purple-100' 
    },
    [USER_ROLES.ADMIN]: { 
      name: 'Admin', 
      color: 'text-red-700', 
      bgColor: 'bg-red-100' 
    },
    [USER_ROLES.PROVIDER]: { 
      name: 'Provider', 
      color: 'text-green-700', 
      bgColor: 'bg-green-100' 
    },
    [USER_ROLES.CUSTOMER]: { 
      name: 'Customer', 
      color: 'text-blue-700', 
      bgColor: 'bg-blue-100' 
    }
  };
  
  return roleDisplayMap[role as keyof typeof roleDisplayMap] || { 
    name: 'Unknown', 
    color: 'text-gray-700', 
    bgColor: 'bg-gray-100' 
  };
};

/**
 * Validate password strength
 */
export const validatePasswordStrength = (password: string) => {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  
  const score = Object.values(checks).filter(Boolean).length;
  
  let strength: 'weak' | 'fair' | 'good' | 'strong' = 'weak';
  if (score >= 4) strength = 'strong';
  else if (score >= 3) strength = 'good';
  else if (score >= 2) strength = 'fair';
  
  return {
    ...checks,
    score,
    strength,
    isValid: checks.minLength && checks.hasUppercase && checks.hasLowercase && checks.hasNumber
  };
};

/**
 * Generate secure random password
 */
export const generateRandomPassword = (length: number = 12): string => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*';
  
  const allChars = lowercase + uppercase + numbers + symbols;
  
  let password = '';
  
  // Ensure at least one character from each category
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];
  
  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

/**
 * Format user registration date
 */
export const formatRegistrationDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
  
  return `${Math.ceil(diffDays / 365)} years ago`;
};

/**
 * Check if user account needs attention
 */
export const getAccountStatus = (user: User | null) => {
  if (!user) return { status: 'logged_out', message: 'Please log in' };
  
  if (!user.email_verified_at) {
    return { 
      status: 'email_verification_required', 
      message: 'Please verify your email address',
      action: 'verify_email'
    };
  }
  
  if (user.role === 'provider' && !user.is_active) {
    return { 
      status: 'pending_approval', 
      message: 'Your provider application is under review',
      action: 'setup_profile'
    };
  }
  
  if (!user.is_active) {
    return { 
      status: 'account_suspended', 
      message: 'Your account has been suspended',
      action: 'contact_support'
    };
  }
  
  return { status: 'active', message: 'Account is active and ready' };
};

/**
 * Get default redirect path based on user role
 */
export const getDefaultRedirectPath = (user: User | null): string => {
  if (!user) return '/login';
  
  const accountStatus = getAccountStatus(user);
  
  if (accountStatus.status === 'email_verification_required') {
    return '/verify-email';
  }
  
  switch (user.role) {
    case USER_ROLES.SUPER_ADMIN:
    case USER_ROLES.ADMIN:
      return '/admin/dashboard';
    case USER_ROLES.PROVIDER:
      if (accountStatus.status === 'pending_approval') {
        return '/provider/services'; // Let them set up while waiting for approval
      }
      return '/provider/dashboard';
    case USER_ROLES.CUSTOMER:
      return '/customer/dashboard';
    default:
      return '/dashboard';
  }
};